import { AxiosError } from 'axios';
import { Helmet } from 'react-helmet-async';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetCompanies } from '../../../api/masters/masters';
import { ErrorInfo, GroupSearchConditions } from '../../../api/model';
import { usePostOrganizationsSearch } from '../../../api/origanization/origanization';
import { useDefaultOptions } from '../../../hooks/api';
import ErrorComponent from '../../atoms/ErrorComponent';
import NoDataComponent from '../../atoms/NoDataComponent';
import WaitComponent from '../../atoms/WaitComponent';
import { OrganizationList } from './OrganizationList';
import { OrganizationSearch } from './OrganizationSearch';

/**
 * タブタイトル
 * @returns
 */
function Title() {
  // 言語情報
  const { t } = useTranslation();
  return (
    <Helmet>
      <title>{t('CMS-POC|グループ一覧')}</title>
    </Helmet>
  );
}
function OrganizationListPage() {
  const {
    i18n: { language },
  } = useTranslation();
  const options = useDefaultOptions(language);

  /* <<<<<<<<<< 会社マスタ取得 */
  const {
    data: companiesData,
    isLoading: companiesIsLoading,
    isError: companiesIsError,
    error: companiesError,
  } = useGetCompanies(options);
  /* >>>>>>>>>> */

  // 検索条件
  const [defaultConditions] = useState<GroupSearchConditions>({});
  const refConditions = useRef<GroupSearchConditions>({});

  /* <<<<<<<<<< 検索 */
  // API
  const {
    data,
    isLoading,
    isError,
    error,
    mutateAsync: searchAsync,
  } = usePostOrganizationsSearch<AxiosError<ErrorInfo>, unknown>(options);
  // 一覧の初回表示
  useEffect(() => {
    const postOrganizations = async () => {
      await searchAsync({ data: defaultConditions });
    };
    void postOrganizations();
  }, [defaultConditions, searchAsync]);
  /* >>>>>>>>>> */

  // 条件を保存し、検索する
  const setConditionsAndSearch = useCallback(
    (next: GroupSearchConditions) => {
      const postOrganizations = async () => {
        // API アクセスが成功した場合
        const onSuccess = () => {
          // sessionStorageUtils.set('GroupSearchConditions.SearchConditions', next);
          refConditions.current = next;
        };
        // API アクセスの実行
        await searchAsync({ data: next }, { onSuccess });
      };
      void postOrganizations();
    },
    [searchAsync]
  );

  // 待ち状態
  if (companiesIsLoading) {
    return (
      <div id="main">
        <WaitComponent />
      </div>
    );
  }

  // エラー
  if (companiesIsError) {
    return <div id="main">{companiesIsError && <ErrorComponent error={companiesError} />}</div>;
  }

  // データが取得できない場合
  if (companiesData == null || companiesData.data.companies == null) {
    return (
      <div id="main">
        <NoDataComponent />
      </div>
    );
  }

  return (
    <div id="main">
      <Title />
      <section>
        <OrganizationSearch
          companies={companiesData?.data.companies}
          defaultConditions={defaultConditions}
          conditionsRef={refConditions}
          setConditionsAndSearch={setConditionsAndSearch}
        />
        <OrganizationList data={data} isLoading={isLoading} isError={isError} error={error} />
      </section>
    </div>
  );
}

export default OrganizationListPage;
